import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash-es';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { CARAMEL_MAKE_AN_OFFER_CTA, CARAMEL_DOMAIN } from 'site-modules/shared/constants/financing/caramel';
import { DismissibleMessage } from 'site-modules/shared/components/private-party/dismissible-message/dismissible-message';

import './caramel-cta.scss';

export const CaramelCta = ({
  dealStatus = '',
  handleCaramelMakeOffer = noop,
  cta = '',
  isPrivateListing = false,
  customStyles = {},
  domainStyles = {},
  buttonClassName = '',
  containerClassName = '',
}) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isDisabled = isLoading || error;

  const resultCta = isPrivateListing ? CARAMEL_MAKE_AN_OFFER_CTA : cta;

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const errorMessage = await handleCaramelMakeOffer();

      if (errorMessage) {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = useCallback(() => {
    setError('');
  }, []);

  return (
    <div
      className={containerClassName}
      style={{
        marginTop: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Button
        style={{
          width: '100%',
          color: '#1b2637',
          background: dealStatus ? '#eef1f6' : 'linear-gradient(90deg, #f5b60a 0%, #feea98 48.96%, #ffda47 100%)',
          borderRadius: '40px',
          padding: '1rem 0',
          fontSize: '1rem',
          textTransform: 'none',
          ...customStyles,
        }}
        disabled={!!dealStatus || isDisabled}
        className={classnames('offer-btn pos-r', buttonClassName, { loading: isLoading })}
        onClick={handleClick}
        {...(!isPrivateListing && { 'data-tracking-id': 'caramel_cta', 'data-tracking-value': cta })}
      >
        <span className={classnames({ 'text-cool-gray-60': isLoading })}>
          {isLoading && (
            <i
              data-testid="btn-spinner"
              className="spinner text-yellow-05 icon-spinner8 me-0_5 d-inline-block"
              aria-hidden
            />
          )}
          {resultCta}
          {isPrivateListing && <i className="icon-open_in_new medium mb-0 ms-0_5" aria-hidden />}
        </span>
      </Button>
      {isPrivateListing && (
        <Button
          disabled={!!dealStatus || isDisabled}
          style={{
            fontSize: '0.875rem',
            marginTop: '0.5rem',
            padding: 0,
            border: 0,
            fontWeight: 'normal',
            ...domainStyles,
          }}
          className="text-lowercase bg-transparent text-info"
          onClick={handleClick}
        >
          {CARAMEL_DOMAIN}
        </Button>
      )}
      {error && (
        <DismissibleMessage
          containerClassName="mt-1 w-100 p-1 border border-danger bg-red-100 text-start text-wrap"
          onClose={handleCloseError}
        >
          <i className="icon-warning2 size-20 text-red-50 align-self-center" aria-hidden />
          <span className="text-sm text-cool-gray-30">{error}</span>
        </DismissibleMessage>
      )}
    </div>
  );
};

CaramelCta.propTypes = {
  dealStatus: PropTypes.string,
  handleCaramelMakeOffer: PropTypes.func,
  cta: PropTypes.string,
  isPrivateListing: PropTypes.bool,
  customStyles: PropTypes.objectOf(PropTypes.string),
  domainStyles: PropTypes.objectOf(PropTypes.string),
  buttonClassName: PropTypes.string,
  containerClassName: PropTypes.string,
};
